import { errorCode, type LiveCopy, type LiveItem, type LiveWhere, type TakeDownReason } from './api'
import { ApiError } from '../api/client'
import { supportsMembership } from '../lib/brief'
import { describeError } from '../lib/describeError'
import { etDate } from './labels'

export const CUSTOM_MAX = 400
export const PAGE_SIZE = 50
export const MEMBERS_LABEL = 'Member exclusives (members)'

/** Where a live article came from: written on the desk, born on the new site, or synced from WordPress. */
export function cameFromLabel(c: LiveItem['came_from']): string {
  return c === 'here' ? 'Written here' : c === 'new_site' ? 'New site' : 'WordPress'
}

export type TakeDownForm = { copies: LiveCopy[]; reason: TakeDownReason | null; customText: string; hasTwin: boolean }

export const REASON_OPTIONS: { key: TakeDownReason; label: string; hint?: string }[] = [
  { key: 'member_archive', label: 'It’s in the member archive', hint: 'Links to the member article and names its issue.' },
  { key: 'out_of_date', label: 'Out of date' },
  { key: 'unavailable', label: 'No longer available' },
  { key: 'custom', label: 'Your own words' },
]

export const REASON_LABEL: Record<string, string> = {
  member_archive: 'Now in the member archive', out_of_date: 'Out of date', unavailable: 'No longer available', custom: 'Your own words',
}

/** "Take down Flight Line" / "Take down members' copy" / "Take down both". */
export function takeDownLabel(copies: LiveCopy[], sectionLabel: string): string {
  const free = copies.includes('free')
  const members = copies.includes('members')
  if (free && members) return 'Take down both'
  if (free) return `Take down ${sectionLabel}`
  if (members) return 'Take down members’ copy'
  return 'Take down'
}

/** The form can be sent: a copy, a reason, words for "your own", and the member-archive
 *  message only ever for the free copy alone, and only when a member copy exists. */
export function canTakeDown(form: TakeDownForm): boolean {
  if (form.copies.length === 0 || form.reason === null) return false
  if (form.reason === 'custom') {
    const n = form.customText.trim().length
    return n > 0 && n <= CUSTOM_MAX
  }
  if (form.reason === 'member_archive') {
    return form.hasTwin && form.copies.length === 1 && form.copies[0] === 'free'
  }
  return true
}

export function tabLabels(sectionLabel: string, membership: boolean): { key: LiveWhere; label: string }[] {
  const tabs: { key: LiveWhere; label: string }[] = [{ key: 'free', label: `${sectionLabel} (free)` }]
  if (membership) tabs.push({ key: 'members', label: MEMBERS_LABEL })
  tabs.push({ key: 'taken_down', label: 'Taken down' })
  return tabs
}

/** Only the tabs this title has; a stale ?where= falls back to Free. */
export function parseWhere(v: string | null, membership: boolean): LiveWhere {
  if (v === 'taken_down') return v
  if (v === 'members' && membership) return v
  return 'free'
}

export const copyLabel = (c: LiveCopy, sectionLabel: string): string => (c === 'free' ? `${sectionLabel} (free)` : MEMBERS_LABEL)
export const copiesLabel = (copies: LiveCopy[], sectionLabel: string): string => copies.map((c) => copyLabel(c, sectionLabel)).join(' and ')

/** What Put back up offers: every copy that is down (both, when the twin is down too), this row's first. */
export function putBackChoices(item: Pick<LiveItem, 'where' | 'down'>): LiveCopy[] {
  const down = item.down ?? []
  if (down.length === 0) return [item.where]
  return [...down].sort((a, b) => (a === item.where ? -1 : b === item.where ? 1 : 0))
}

/** What Take down offers: the copies still up (a twin already down is not offered again). */
export const takeDownChoices = (item: Pick<LiveItem, 'copies' | 'down'>): LiveCopy[] =>
  item.copies.filter((c) => !(item.down ?? []).includes(c))

export const PUT_BACK_FAILED = 'Couldn’t put it back up — try again in a minute.'
export const NO_CONNECTION = 'Couldn’t reach the server — check your connection and try again.'

/** What a failed Put back up says. The server words every refusal ({code, message});
 *  a bare 5xx (a proxy page, a crash) or a dropped connection gets a plain sentence too,
 *  never "API error 502" or "Failed to fetch". */
export function putBackErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (errorCode(err) !== null) return describeError(err)
    const d = typeof err.detail === 'object' && err.detail !== null ? (err.detail as { detail?: unknown }).detail : undefined
    if (err.status >= 500 || d === undefined) return PUT_BACK_FAILED
    return describeError(err)
  }
  if (err instanceof TypeError) return NO_CONNECTION   // fetch() rejects with a TypeError offline
  return err instanceof Error && err.message ? err.message : PUT_BACK_FAILED
}

/** The piece went up while the screen still offered Put back up: reload what is shown. */
export const putBackIsStale = (err: unknown): boolean => errorCode(err) === 'already_live'

export const hasTwin = (item: LiveItem): boolean => item.twin !== null

export const sinceLabel = (iso: string | null): string => (iso ? etDate(iso) : '—')
export { supportsMembership }
